/**
 * Card detail (§13.3) — "hero card visual + facts; tabs: current cycle ·
 * statement history · caps · cashback trend · card settings".
 *
 * Tabs: **statement history**, **transactions**, **rewards** (Phase 7's caps,
 * learned rate and estimate-vs-actual) and **cashback** — this card's slice of
 * the Real Cashback Ledger, month by month, from the same component and the
 * same endpoint as the Cashback page, so the two cannot disagree.
 *
 * Payment status carries its basis on hover, and can be overridden, because
 * §12 requires both: a derived status the user cannot interrogate or correct is
 * one they have to take on faith.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, FileText, Info, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { RewardsTab } from "@/components/caps/RewardsTab";
import { MonthlyBreakdown } from "@/components/cashback/MonthlyBreakdown";
import { CardObject } from "@/components/cards/CardObject";
import { PhoneTxnList } from "@/components/transactions/PhoneTxnList";
import { Tilt } from "@/components/ui/motion";
import { Badge, Button, EmptyState, Panel } from "@/components/ui/primitives";
import {
  cardsApi,
  cashbackApi,
  cyclesApi,
  statementsApi,
  transactionsApi,
  type PaymentStatus,
  type Statement,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import { useIsPhone } from "@/lib/useMedia";
import { haptic } from "@/platform";

type Tab = "statements" | "transactions" | "rewards" | "cashback";

const TABS: { id: Tab; label: string }[] = [
  { id: "statements", label: "Statements" },
  { id: "transactions", label: "Transactions" },
  // §13.3 calls this tab "caps". It holds more than caps now — the learned rate
  // and the estimate-vs-actual check too — so it is named for what it answers.
  { id: "rewards", label: "Rewards" },
  { id: "cashback", label: "Cashback" },
];

export function CardDetailPage() {
  const { id = "" } = useParams();
  // Deep-linkable: the card wall's headroom bars open straight onto Rewards.
  const phone = useIsPhone();
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).get("tab") === "rewards" ? "rewards" : "statements",
  );

  const { data: card, isLoading } = useQuery({
    queryKey: ["card", id],
    queryFn: () => cardsApi.get(id),
    enabled: Boolean(id),
  });

  const { data: statements } = useQuery({
    queryKey: ["statements", id],
    queryFn: () => statementsApi.list({ card_id: id }),
    enabled: Boolean(id),
  });

  const { data: txns } = useQuery({
    queryKey: ["transactions", id],
    queryFn: () => transactionsApi.list({ card_id: [id], limit: 100 }),
    enabled: Boolean(id) && tab === "transactions",
  });

  // The whole wallet's cycles, filtered to this card. One shared query key means
  // the Cycles page and this page cannot show different dates for the same card.
  const { data: cycles } = useQuery({ queryKey: ["cycles"], queryFn: cyclesApi.list });
  const cycle = cycles?.cards.find((c) => c.card_id === id);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="skeleton h-4 w-24" />
        <div className="grid gap-6 sm:grid-cols-[minmax(0,22rem)_1fr]">
          <div className="skeleton aspect-card w-full rounded-card" />
          <div className="space-y-3">
            <div className="skeleton h-6 w-48" />
            <div className="skeleton h-4 w-full" />
            <div className="skeleton h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="mx-auto max-w-5xl">
        <Panel>
          <EmptyState
            title="Card not found"
            body="This card may have been deleted."
            action={
              <Button asChild variant="default" size="sm">
                <Link to="/cards">Back to cards</Link>
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  const latest = statements?.[0];

  return (
    <div className="mx-auto max-w-6xl space-y-10">
      <Link
        to="/cards"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All cards
      </Link>

      {/* --- Hero -------------------------------------------------------- */}
      <div className="grid items-start gap-8 md:grid-cols-[minmax(0,24rem)_1fr] md:gap-10">
        <div className="relative">
          {/* The card's own colour, thrown as light onto the page behind it. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-10 -z-10 opacity-50 blur-3xl"
            style={{
              background: card.theme
                ? `radial-gradient(closest-side, ${card.theme.gradient_to}, transparent)`
                : undefined,
            }}
          />
          <motion.div layoutId={`card-${card.id}`} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
            <Tilt>
              <CardObject card={card} outstanding={latest?.total_amount_due ?? null} />
            </Tilt>
          </motion.div>
        </div>

        <div className="animate-rise space-y-5">
          <div>
            <div className="label-micro">{card.issuer} · •{card.last4}</div>
            <h1 className="mt-2 text-3xl font-medium tracking-[-0.04em] text-ink sm:text-[2.5rem] sm:leading-[1.05]">
              {card.nickname || card.product_name || card.issuer}
            </h1>
            <p className="mt-2 text-sm text-muted">
              {card.product_name && card.product_name !== card.nickname
                ? `${card.product_name} · `
                : ""}
              {card.issuer}
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Fact label="Credit limit" value={formatMoney(card.credit_limit)} pinned={card.credit_limit_pinned} />
            <Fact
              label="Available"
              value={formatMoney(latest?.available_limit)}
              note={latest ? `as of ${formatDate(latest.statement_date)}` : undefined}
            />
            <Fact
              label="Statement day"
              value={card.billing_day ? `${ordinal(card.billing_day)} of the month` : "—"}
              pinned={card.billing_day_pinned}
            />
            <Fact
              label="Due day"
              value={card.due_day ? `${ordinal(card.due_day)} of the month` : "—"}
              pinned={card.due_day_pinned}
            />
          </dl>

          {/* The cycle, projected from this card's own statement history. Kept
              below the printed facts and marked as projected, because that is
              exactly what separates it from them (§2.7). The `due_day` above is
              what the last statement happened to say; `Bill due` here is what
              the learned rule predicts, and on some cards they differ. */}
          {cycle?.cycle_end && (
            <div className="rounded-xl border border-dashed border-hairline-strong bg-raised/30 p-4">
              <div className="flex items-baseline justify-between gap-2">
                <span className="label-micro">Cycle now</span>
                <Link to="/cycles" className="text-xs text-brass hover:underline">
                  All cycles →
                </Link>
              </div>
              <dl className="mt-2 grid grid-cols-3 gap-x-4 gap-y-1">
                <Fact
                  label="Closes"
                  value={formatDate(cycle.cycle_end)}
                  note={
                    cycle.days_left_in_cycle === 0
                      ? "today"
                      : `${cycle.days_left_in_cycle}d left`
                  }
                />
                <Fact
                  label="Bill due"
                  value={formatDate(cycle.due_expected_on)}
                  note="projected"
                />
                <Fact
                  label="Float today"
                  value={cycle.float_days !== null ? `${cycle.float_days} days` : "—"}
                  note="interest-free"
                />
              </dl>
              <p className="mt-2 text-xs text-faint">
                {cycle.close_basis} {cycle.due_basis}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* --- Tabs -------------------------------------------------------- */}
      {/* Desktop: an underlined tab row. Phone (mobile M3): the same tabs as
          a segmented control floating just above the tab bar, under the thumb. */}
      <div className="border-b border-hairline max-md:fixed max-md:inset-x-3 max-md:bottom-[calc(4.5rem+var(--inset-bottom))] max-md:z-30 max-md:rounded-2xl max-md:border max-md:bg-panel/90 max-md:p-1 max-md:shadow-lift max-md:backdrop-blur-xl">
        <div className="flex gap-1 overflow-x-auto max-md:gap-0.5" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => {
                haptic("tick");
                setTab(t.id);
              }}
              className={cn(
                "relative whitespace-nowrap px-3.5 pb-3 pt-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50",
                "max-md:min-h-11 max-md:flex-1 max-md:rounded-xl max-md:px-2 max-md:py-0 max-md:text-[13px]",
                tab === t.id ? "text-ink max-md:bg-raised" : "text-muted hover:text-ink",
              )}
            >
              {t.label}
              {tab === t.id && (
                <motion.span
                  layoutId="card-tab"
                  aria-hidden
                  className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-brass max-md:hidden"
                  transition={{ type: "spring", stiffness: 480, damping: 36 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === "statements" && <StatementHistory statements={statements} cardId={id} />}

      {tab === "transactions" && (
        <Panel className="overflow-hidden">
          {!txns ? (
            <div className="space-y-2 p-5">
              <div className="skeleton h-9 w-full" />
              <div className="skeleton h-9 w-full" />
            </div>
          ) : txns.items.length === 0 ? (
            <EmptyState
              title="No transactions yet"
              body="Transactions appear as this card's statements are parsed."
            />
          ) : (
            <>
              {phone ? (
                <div className="px-4">
                  <PhoneTxnList rows={txns.items} cards={card ? [card] : []} />
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[34rem] text-sm">
                  <thead>
                    <tr className="border-b border-hairline text-left">
                      <th className="label-micro px-5 py-2 font-normal">Date</th>
                      <th className="label-micro px-3 py-2 font-normal">Description</th>
                      <th className="label-micro px-5 py-2 text-right font-normal">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {txns.items.map((t) => (
                      <tr key={t.id}>
                        <td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-muted">
                          {formatDate(t.txn_date)}
                        </td>
                        <td className="px-3 py-2.5 text-ink">{t.description_raw}</td>
                        <td
                          className={cn(
                            "whitespace-nowrap px-5 py-2.5 text-right font-medium tabular-nums",
                            t.direction === "credit" ? "text-paid" : "text-ink",
                          )}
                        >
                          {t.direction === "credit" ? "+" : ""}
                          {formatMoney(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {txns.total > txns.items.length && (
                <div className="border-t border-hairline px-5 py-3 text-xs text-faint">
                  Showing {txns.items.length} of {txns.total}.{" "}
                  <Link to="/transactions" className="text-brass hover:underline">
                    See all in Transactions →
                  </Link>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {tab === "rewards" && <RewardsTab cardId={id} statements={statements} />}

      {tab === "cashback" && <CardCashback cardId={id} />}

      {/* Room for the floating tab control on a phone. */}
      <div className="h-16 md:hidden" aria-hidden />
    </div>
  );
}

/** This card's cashback. The current fiscal year comes from the ledger itself
 *  (it knows the FY boundary), never computed here. */
function CardCashback({ cardId }: { cardId: string }) {
  const { data, isError, error, refetch } = useQuery({
    queryKey: ["cashback", "summary", "current"],
    queryFn: () => cashbackApi.summary(),
  });

  if (isError) {
    return (
      <Panel className="flex items-center justify-between gap-3 p-5">
        <p className="text-sm text-muted">
          Couldn&rsquo;t load cashback. {(error as Error).message}
        </p>
        <Button variant="default" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </Panel>
    );
  }
  if (!data) return <div className="skeleton h-40 w-full rounded-xl" />;

  return (
    <div className="space-y-3">
      <MonthlyBreakdown fiscalYear={data.fiscal_year} cardId={cardId} />
      <p className="text-xs text-faint">
        Every card, fees and exports are on the{" "}
        <Link to="/cashback" className="text-brass hover:underline">
          Cashback page →
        </Link>
      </p>
    </div>
  );
}

function StatementHistory({
  statements,
  cardId,
}: {
  statements: Statement[] | undefined;
  cardId: string;
}) {
  if (!statements) {
    return (
      <Panel className="space-y-2 p-5">
        <div className="skeleton h-9 w-full" />
        <div className="skeleton h-9 w-full" />
      </Panel>
    );
  }

  if (statements.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={FileText}
          title="No statements yet"
          body="Drop a PDF on the Statements page, or let email sync bring them in."
          action={
            <Button asChild variant="default" size="sm">
              <Link to="/statements">Go to statements</Link>
            </Button>
          }
        />
      </Panel>
    );
  }

  return (
    <Panel className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[42rem] text-sm">
          <thead>
            <tr className="border-b border-hairline text-left">
              <th className="label-micro px-5 py-2 font-normal">Statement</th>
              <th className="label-micro px-3 py-2 font-normal">Due</th>
              <th className="label-micro px-3 py-2 text-right font-normal">Total due</th>
              <th className="label-micro px-3 py-2 text-right font-normal">Min due</th>
              <th className="label-micro px-3 py-2 text-right font-normal">Txns</th>
              <th className="label-micro px-5 py-2 font-normal">Payment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {statements.map((s) => (
              <tr key={s.id}>
                <td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-ink">
                  {formatDate(s.statement_date)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted">
                  {s.payment_due_date ? formatDate(s.payment_due_date) : "—"}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right font-medium tabular-nums text-ink">
                  {formatMoney(s.total_amount_due)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-muted">
                  {formatMoney(s.minimum_amount_due)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-muted">
                  {s.transaction_count}
                </td>
                <td className="px-5 py-2.5">
                  <PaymentCell statement={s} cardId={cardId} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const STATUS_TONE: Record<PaymentStatus, "paid" | "warn" | "overdue" | "neutral"> = {
  paid: "paid",
  partial: "warn",
  unpaid: "overdue",
  unknown: "neutral",
};

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: "Paid",
  partial: "Partial",
  unpaid: "Unpaid",
  unknown: "Unconfirmed",
};

function PaymentCell({ statement, cardId }: { statement: Statement; cardId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const status = (statement.payment_status_override ??
    statement.payment_status) as PaymentStatus;

  const set = useMutation({
    mutationFn: (next: string) => statementsApi.setPaymentStatus(statement.id, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["statements", cardId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      toast.success("Payment status updated.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="relative flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50"
        title={
          statement.payment_status_override
            ? "You set this by hand. Click to change."
            : (statement.payment_status_basis ?? "Click to override.")
        }
      >
        <Badge tone={STATUS_TONE[status]}>
          {STATUS_LABEL[status]}
          {statement.payment_status_override && "*"}
        </Badge>
      </button>

      {statement.payment_status_basis && !statement.payment_status_override && (
        <Info className="h-3 w-3 shrink-0 text-faint" aria-hidden />
      )}

      {open && (
        <div className="absolute right-0 top-7 z-10 w-44 rounded-lg border border-hairline bg-panel p-1 shadow-lg">
          {(["paid", "partial", "unpaid", "unknown", "auto"] as const).map((option) => (
            <button
              key={option}
              type="button"
              disabled={set.isPending}
              onClick={() => set.mutate(option)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-muted transition-colors hover:bg-raised hover:text-ink"
            >
              {set.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : option === "auto" ? (
                <Check className="h-3 w-3 opacity-0" aria-hidden />
              ) : (
                <Check
                  className={cn("h-3 w-3", status === option ? "opacity-100" : "opacity-0")}
                  aria-hidden
                />
              )}
              {option === "auto" ? "Let MyDeck decide" : STATUS_LABEL[option]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  note,
  pinned,
}: {
  label: string;
  value: string;
  note?: string;
  pinned?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="label-micro">
        {label}
        {pinned && (
          <span className="ml-1.5 normal-case tracking-normal text-brass" title="You set this by hand; statements won't overwrite it.">
            pinned
          </span>
        )}
      </dt>
      <dd className="mt-0.5 text-sm tabular-nums text-ink">{value}</dd>
      {note && <dd className="text-xs text-faint">{note}</dd>}
    </div>
  );
}

function ordinal(n: number): string {
  const suffix = ["th", "st", "nd", "rd"][(n % 100 - 20) % 10] ?? ["th", "st", "nd", "rd"][n % 100] ?? "th";
  return `${n}${suffix}`;
}
